import {
  GroupByMode,
  ID,
  LinkedItemSection,
  rootId,
  SortingMode,
  Tag,
  Task,
  TaskGroupBy,
  TaskListItemTypes,
  TaskListTreeItem,
  TaskPriorityOptions,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { buildDeadlineDeadlineTree } from './build-deadline-deadline-tree';
import { buildDeadlineTree } from './build-deadline-tree';
import { buildGoalTasksTree } from './build-goal-tasks-tree';
import { buildPriorityTree } from './build-priority-tree';
import { buildSectionTree } from './build-section-tree';
import { buildTagTree } from './build-tag-tree';
import { buildTasksTree } from './build-tasks-tree';
import { buildTypeTree } from './build-type-tree';

export type GetOrderedTaskListTasksOptions = {
  groupByMode: TaskGroupBy;
  tasks: Task[];
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  sections?: LinkedItemSection[] | null;
  goals?: { id: ID; name: string }[];
  tags?: Tag[];
  relationshipsMap?: Record<ID, ID[]>;
  customOrder?: Record<ID, ID[]>;
  // Goal grouping only: drop tasks whose goal isn't in `goals` instead of
  // bucketing them under a no-goal group (matches the goal-tasks list).
  hideNoGoalGroup?: boolean;
};

// Group names never affect ordering — only the data does — so the dispatcher
// passes empty labels and lets each builder partition/sort by its inputs.
const emptyPriorityLabels: Record<TaskPriorityOptions, string> = {
  [TaskPriorityOptions.None]: '',
  [TaskPriorityOptions.High]: '',
  [TaskPriorityOptions.Medium]: '',
  [TaskPriorityOptions.Low]: '',
};

const buildTree = ({
  groupByMode,
  tasks,
  sortingMode,
  today,
  weekStartsOn,
  sections,
  goals,
  tags,
  relationshipsMap,
  customOrder,
  hideNoGoalGroup,
}: GetOrderedTaskListTasksOptions): Record<ID, TaskListTreeItem> => {
  const shared = {
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    hideNewTaskRow: true,
    rootLabel: '',
    newTaskLabel: '',
  };

  if (groupByMode === GroupByMode.Section) {
    return buildSectionTree({
      ...shared,
      sections: sections ?? null,
      showEmptyGroups: false,
      noSectionLabel: '',
    });
  }

  if (groupByMode === GroupByMode.Goal) {
    return buildGoalTasksTree({
      ...shared,
      goals: goals ?? [],
      showEmptyGroups: false,
      hideNoGoalGroup,
      inboxLabel: '',
    });
  }

  if (groupByMode === GroupByMode.Priority) {
    return buildPriorityTree({
      ...shared,
      showEmptyGroups: false,
      priorityLabels: emptyPriorityLabels,
    });
  }

  if (groupByMode === GroupByMode.Type) {
    return buildTypeTree({
      ...shared,
      showEmptyGroups: false,
      tasksLabel: '',
      habitsLabel: '',
      newHabitLabel: '',
    });
  }

  if (groupByMode === GroupByMode.Deadline) {
    return buildDeadlineTree({
      ...shared,
      showEmptyOverdue: false,
      showEmptyNoDate: false,
      showEmptyDate: false,
      overdueLabel: '',
      noDeadlineLabel: '',
    });
  }

  if (groupByMode === GroupByMode.DeadlineDeadline) {
    return buildDeadlineDeadlineTree({
      ...shared,
      showEmptyGroups: false,
      overdueLabel: '',
      noDeadlineLabel: '',
    });
  }

  if (groupByMode === GroupByMode.Tag) {
    return buildTagTree({
      ...shared,
      tags: tags ?? [],
      relationshipsMap: relationshipsMap ?? {},
      showEmptyGroups: false,
      untaggedLabel: '',
    });
  }

  return buildTasksTree(shared);
};

const collectOrderedTaskIds = (tree: Record<ID, TaskListTreeItem>): ID[] => {
  const orderedIds: ID[] = [];

  const visit = (itemId: ID) => {
    const item = tree[itemId];
    if (!item) {
      return;
    }
    if (item.type === TaskListItemTypes.Task) {
      orderedIds.push(item.id);
      return;
    }
    item.children?.forEach(visit);
  };

  tree[rootId]?.children?.forEach(visit);
  return orderedIds;
};

// Returns the life-area/task-list tasks in the exact order the grouped list
// renders them (group partition + within-group sort), flattened to a single
// array. Lets a preview surface show the same "first N" the full list shows.
export const getOrderedTaskListTasks = (
  options: GetOrderedTaskListTasksOptions,
): Task[] => {
  const tree = buildTree(options);
  const orderedIds = collectOrderedTaskIds(tree);
  const tasksById = options.tasks.reduce<Record<ID, Task>>((acc, task) => {
    acc[task.id] = task;
    return acc;
  }, {});

  return orderedIds
    .map((taskId) => tasksById[taskId])
    .filter((task): task is Task => !!task);
};
